import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { getApiEndpoint } from "../../config/config";
import PageContent from "../shared/PageContent/PageContent";
import PinGate from "../shared/PinGate/PinGate";
import {
  isPinRequiredResponse,
  isPinUnlockSessionActive,
  pinUnlockHeaders,
} from "../../utils/pinUnlockSession";
import {
  Stack,
  Card,
  Table,
  Text,
  Button,
  Alert,
  Loader,
  Center,
} from "@mantine/core";
import { IconArrowLeft, IconAlertCircle } from "@tabler/icons-react";

const formatLabel = key =>
  key.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());

const formatValue = value => {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "object") return JSON.stringify(value, null, 2);
  return String(value);
};

function UserPayDetailsPage() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { getVerifiedJWT } = useAuth();

  const [payDetails, setPayDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Pay is PIN-gated server-side, so there is nothing to fetch while locked —
  // PinGate is showing the prompt instead.
  const fetchPayDetails = useCallback(async () => {
    if (!isPinUnlockSessionActive()) return;

    try {
      setLoading(true);
      setError(null);

      const jwt = await getVerifiedJWT();
      if (!jwt) {
        setError("Authentication failed");
        return;
      }

      const response = await fetch(getApiEndpoint(`/v1/pyr/user/${userId}`), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
          ...pinUnlockHeaders(),
        },
      });

      if (response.ok) {
        const result = await response.json();
        setPayDetails(result.data ?? result);
      } else {
        const body = await response.json().catch(() => ({}));
        if (isPinRequiredResponse(response, body)) {
          setPayDetails(null);
          return;
        }
        setError("Failed to fetch pay details");
      }
    } catch (err) {
      setError("Failed to fetch pay details");
    } finally {
      setLoading(false);
    }
  }, [userId, getVerifiedJWT]);

  useEffect(() => {
    fetchPayDetails();
  }, [fetchPayDetails]);

  const headerActions = (
    <Button
      variant="outline"
      leftSection={<IconArrowLeft size={16} />}
      onClick={() => navigate(-1)}
    >
      Back
    </Button>
  );

  const renderBody = () => {
    if (loading) {
      return (
        <Center py="xl">
          <Loader size="lg" />
        </Center>
      );
    }

    if (error) {
      return (
        <Alert icon={<IconAlertCircle size={16} />} color="red" title="Error">
          {error}
        </Alert>
      );
    }

    if (!payDetails || Object.keys(payDetails).length === 0) {
      return (
        <Text ta="center" c="dimmed" py="xl">
          No pay details found.
        </Text>
      );
    }

    return (
      <Card withBorder radius="md" p="lg">
        <Table verticalSpacing="sm" horizontalSpacing="md">
          <Table.Tbody>
            {Object.entries(payDetails).map(([key, value]) => (
              <Table.Tr key={key}>
                <Table.Td style={{ fontWeight: 500, width: "40%" }}>
                  {formatLabel(key)}
                </Table.Td>
                <Table.Td style={{ whiteSpace: "pre-wrap" }}>
                  {formatValue(value)}
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Card>
    );
  };

  return (
    <PageContent
      title="Pay Details"
      subtitle="User compensation details"
      headerActions={headerActions}
    >
      <Stack>
        <PinGate
          description="Enter your 4-digit security PIN to view this user's pay details."
          onUnlock={fetchPayDetails}
        >
          {renderBody()}
        </PinGate>
      </Stack>
    </PageContent>
  );
}

export default UserPayDetailsPage;
